import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Leaf, Users, TrendingUp, FileText, Globe, Zap, Heart, ShieldCheck, ArrowRight, TreePine, Recycle } from 'lucide-react';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { MemberMap } from './MemberMap';
import GalleryCarousel from './GalleryCarousel';

interface HomePageProps {
  onPageChange: (page: string) => void;   
}

export default function HomePage({ onPageChange }: HomePageProps) {
  const statistics = [
    {
      number: 16,
      suffix: "",
      label: "Member Countries",  
      icon: <Globe className="w-6 h-6" />,
      bgColor: "bg-leaf-green/10",
      textColor: "text-leaf-green",
      hoverBg: "bg-leaf-green",
      hoverText: "text-white",
    },
    {
      number: 70,
      suffix: "+",
      label: "Years of Experience",
      icon: <ShieldCheck className="w-6 h-6" />,
      bgColor: "bg-amber-100",
      textColor: "text-amber-800",
      hoverBg: "bg-amber-800",
      hoverText: "text-white",
    },
    {
      number: 12.5,
      suffix: "M",
      label: "Tonnes of Sugar",
      icon: <TrendingUp className="w-6 h-6" />,
      bgColor: "bg-teal-100",
      textColor: "text-teal-600",
      hoverBg: "bg-teal-600",
      hoverText: "text-white",
    },
    {
      number: 850,
      suffix: "K",
      label: "Jobs Supported",
      icon: <Users className="w-6 h-6" />,
      bgColor: "bg-lime-100",
      textColor: "text-lime-600",
      hoverBg: "bg-lime-600",
      hoverText: "text-white",
    },
  ];

  const focusAreas = [
  {
    title: "Trade Advocacy",
    description: "Championing fair trade policies and market access for sustainable sugar industries worldwide.",
    icon: <Globe className="w-6 h-6" />,
    color: "bg-yellow-400",
    image: "https://images.unsplash.com/photo-1605745341075-1b7460b99df8?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D", 
  },
  {
    title: "Community Development",
    description: "Supporting rural communities through education, healthcare, and infrastructure development.",
    icon: <Heart className="w-6 h-6" />,
    color: "bg-rose-400",
    image: "https://images.unsplash.com/photo-1722643882339-7a6c9cb080db?q=80&w=1332&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
  },
  {
    title: "Sustainable Agriculture",
    description: "Promoting environmentally responsible farming practices and climate adaptation strategies.",
    icon: <TreePine className="w-6 h-6" />,
    color: "bg-green-400",
    image: "https://images.unsplash.com/photo-1542594631-18ac04725612?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
  },
  {
    title: "Innovation & Research",
    description: "Advancing sugar industry technology and providing comprehensive market intelligence.",
    icon: <Zap className="w-6 h-6" />,
    color: "bg-blue-400",
    image: "https://images.unsplash.com/photo-1631557676757-fcc7b1160be8?q=80&w=1191&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
  },
];

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });


  return (
    <div className="min-h-screen">
      {/* Hero Section - Adjusted for floating nav */}
    <section
      className="relative min-h-screen flex items-center justify-center mt-2 mx-4 sm:mx-6 lg:mx-5 p-6 rounded-3xl overflow-hidden shadow-xl"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1667966054415-243ecbfd55f5?q=80&w=1888&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D')`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
      }}
    >
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-forest-green/20 via-sugar-cane/10 to-leaf-green/20 backdrop-brightness-90 rounded-3xl"></div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl lg:text-6xl font-bold mb-8 leading-tight landing-page-header">
            SUGAR,
            <br /> <span className="text-amber-300 landing-page-header"> A CORNERSTONE</span><br /> FOR DEVELOPMENT IN
            <br />ACP & LDC COUNTRIES
          </h1>

          <div className="flex flex-col sm:flex-row gap-6 justify-center mt-10">
            <Button
              size="lg"
              onClick={() => onPageChange("about")}
              className="action bg-leaf-green hover:bg-lime-600 text-white !px-12 py-6 rounded-3xl text-lg font-semibold shadow-2xl hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              Discover Our Story
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => onPageChange("data-explorer")}
              className="action border-2 border-white text-white hover:bg-white hover:text-forest-green !px-12 py-6 rounded-3xl text-lg font-semibold backdrop-blur-sm bg-white/10 transition-all duration-300"
            >
              Explore Data
            </Button>
          </div>
        </div>
      </div>
    </section>

    {/* Statistics Section */}
    <section ref={ref} className="py-20 bg-gradient-to-r from-cream to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-forest-green mb-4">Impact by Numbers</h2>
            <p className="text-xl text-sugar-cane max-w-2xl mx-auto">
              Decades of commitment to sustainable sugar industry development across our member countries
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {statistics.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                  <div
                    className={`${stat.bgColor} w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 ${stat.textColor} group-hover:${stat.hoverBg} group-hover:${stat.hoverText} transition-all duration-300`}
                  >
                    {stat.icon}
                  </div>
                  <div className="text-4xl font-bold text-forest-green mb-2">
                    {inView && (
                      <CountUp end={stat.number} duration={2} decimals={stat.number % 1 !== 0 ? 1 : 0} suffix={stat.suffix} />
                    )}
                  </div>
                  <div className="text-sugar-cane font-medium">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
      </div>
    </section>


      {/* Focus Areas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-forest-green mb-4">Our Impact Areas</h2>
            <p className="text-xl text-sugar-cane max-w-3xl mx-auto">
              We work across multiple sectors to support and advocate for sustainable sugar industry development
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {focusAreas.map((area, index) => (
              <div
                key={index}
                className="relative bg-white rounded-3xl shadow-lg group overflow-hidden flex flex-col justify-between transition-all duration-300 hover:shadow-2xl hover:scale-105"
              >
                 {/* Icon Overlap */}
                <div className="absolute top-30 right-5  z-20">
                  <div className={`${area.color} text-white p-4 rounded-xl shadow-md group-hover:scale-105 transition-transform duration-300`}>
                    {area.icon}
                  </div>
                </div>

                {/* Image Section */}
                <div className="w-full h-40 mt-auto">
                  <ImageWithFallback
                    src={area.image}
                    alt={area.title}
                    style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                    className="object-cover"
                  />
                </div>

                {/* Text Content */}
                <div className="p-6 pb-10 z-10">
                  <h3 className="text-lg font-semibold text-forest-green mb-2">{area.title}</h3>
                  <p className="text-slate-700 text-sm leading-relaxed mb-4 line-clamp-3">
                    {area.description}
                  </p>
                  <a
                    href="#"
                    className="text-orange-500 font-medium hover:underline text-sm flex align-center gap-2"
                  >
                    Read more
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Member Countries Showcase */}
      <MemberMap />

      {/* Carousel */}
      <section className='bg-amber-50 py-20'>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-forest-green mb-4">Our Highlights</h2>
            <p className="text-xl text-sugar-cane max-w-3xl mx-auto">
              Moments captured throughout our journey of sustainable sugar industry development
            </p>
          </div>
        </div>
        <GalleryCarousel />
      </section>

      {/* Visual Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-forest-green mb-8">
                From Field to Market:<br />
                <span className="text-leaf-green">A Legacy of Excellence</span>
              </h2>
              <p className="text-lg text-slate-700 mb-8 leading-relaxed">
                For over seven decades, we've been the voice of sugar industries across developing nations, 
                advocating for fair trade, sustainable practices, and community development. Our work spans 
                from supporting smallholder farmers to negotiating international trade agreements.
              </p>
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-amber-400/10 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-forest-green">Economic Growth</h4>
                    <p className="text-slate-700">Driving GDP and export earnings across member countries</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-teal-600/10 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-forest-green">Community Impact</h4>
                    <p className="text-slate-700">Supporting millions of livelihoods in rural areas</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-green-600/10 rounded-xl flex items-center justify-center">
                    <Recycle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-forest-green">Sustainability</h4>
                    <p className="text-slate-700">Promoting environmentally responsible practices</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1625246333195-78d9c38ad449?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
                  alt="Sugar processing facility"
                  className="rounded-2xl shadow-lg w-full h-48 object-cover"
                />
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1559027615-cd4628902d4a?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
                  alt="Community gathering"
                  className="rounded-2xl shadow-lg w-full h-48 object-cover mt-8"
                />
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1614595302403-444d33a6fd46?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                  alt="Traditional sugar making"
                  className="rounded-2xl shadow-lg w-full h-48 object-cover -mt-8"
                />
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1565870783541-ee598acdb28b?q=80&w=735&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                  alt="Sustainable farming"
                  className="rounded-2xl shadow-lg w-full h-48 object-cover"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-golden-harvest/20 rounded-full blur-xl"></div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-leaf-green/20 rounded-full blur-xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 agricultural-gradient text-white relative overflow-hidden">
        <div className="absolute inset-0 nature-pattern"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Join the Future of Sustainable Sugar
          </h2>
          <p className="text-xl mb-12 max-w-3xl mx-auto text-cream/90 leading-relaxed">
            Stay connected with the latest developments, research, and opportunities in the global sugar industry. 
            Together, we're building a more sustainable and prosperous future for agricultural communities worldwide.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button 
              size="lg"
              onClick={() => onPageChange('updates')}
              className="bg-white text-forest-green hover:bg-cream !px-12 py-6 rounded-3xl action text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
            >
              Latest Updates
              <FileText className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => onPageChange('contact')}
              className="border-2 border-white text-white hover:bg-white hover:text-forest-green !px-12 py-6 rounded-3xl action text-lg font-semibold backdrop-blur-sm bg-white/10 transition-all duration-300"
            >
              Get Involved
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}